import { useState } from "react";
import { api } from "../api";
import type { BootstrapResponse } from "../types";
import { Feedback } from "./ui/Surface";

/**
 * Closing the shop for a day is a one-line fact: which day, and what to call it.
 *
 * The full month grid exists (public holidays, ranges, deadline overlays) but it fills the
 * dialog for a decision this small. A date field and a name are enough; registered days
 * list underneath as chips, which is also how you remove one.
 */
export function WorkshopHolidayRow({ data, refresh, actorName }: { data: BootstrapResponse; refresh: () => Promise<void>; actorName: string }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const holidays = [...data.workshopHolidays].sort((a, b) => (a.dates[0] ?? "").localeCompare(b.dates[0] ?? ""));

  // A shop closure is normally a stretch of days, not one: the range expands to the list
  // of dates the planner actually reads.
  const range = (() => {
    if (!from) return [];
    const last = to && to >= from ? to : from;
    const dates: string[] = [];
    for (let at = Date.parse(`${from}T00:00:00Z`); at <= Date.parse(`${last}T00:00:00Z`); at += 86_400_000) {
      dates.push(new Date(at).toISOString().slice(0, 10));
    }
    return dates;
  })();

  const submit = async () => {
    if (!range.length || !name.trim() || busy) return;
    setBusy(true); setError("");
    try {
      await api.createWorkshopHoliday({ dates: range, startTime: "00:00", endTime: "24:00", name: name.trim(), createdBy: actorName });
      setFrom(""); setTo(""); setName("");
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "휴무일을 등록하지 못했습니다.");
    } finally { setBusy(false); }
  };

  const remove = async (id: string) => {
    if (busy) return;
    setBusy(true); setError("");
    try {
      await api.deleteWorkshopHoliday(id);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "휴무일을 삭제하지 못했습니다.");
    } finally { setBusy(false); }
  };

  /* 2026-10-19 밤 지윤: claude.ai 설정 General 문법 — 위에 이름 붙은 칸들을 걷고 줄로 선다. 첫 줄은 「휴무일 추가」
     (오른쪽에 시작 ~ 끝 · 이름 · 추가), 그 아래 등록된 휴무일이 한 줄씩(이름 / 날짜, 오른쪽 삭제). */
  return <>
    <form className="account-settings-row holiday-add-row" onSubmit={event => { event.preventDefault(); void submit(); }}>
      <div><span>휴무일 추가</span><p>쉬는 날은 배치에서 빠집니다.</p></div>
      <div className="holiday-add-controls">
        <input className="account-settings-input holiday-date" type="date" aria-label="시작일" value={from} disabled={busy} onChange={event => setFrom(event.target.value)} />
        <span className="holiday-sep" aria-hidden="true">~</span>
        <input className="account-settings-input holiday-date" type="date" aria-label="종료일" value={to} min={from || undefined} disabled={busy} onChange={event => setTo(event.target.value)} />
        <input className="account-settings-input holiday-name" aria-label="휴무일명" placeholder="이름" value={name} maxLength={100} disabled={busy} onChange={event => setName(event.target.value)} />
        <button type="submit" className="account-settings-button" disabled={busy || !range.length || !name.trim()}>{busy ? "저장 중…" : "추가"}</button>
      </div>
    </form>
    {error && <Feedback tone="error">{error}</Feedback>}
    {holidays.map(holiday => <div className="account-settings-row" key={holiday.id}>
      <div><span>{holiday.name}</span><p>{holiday.dates.length > 1 ? `${holiday.dates[0]} ~ ${holiday.dates.at(-1)}` : holiday.dates[0]}</p></div>
      <button type="button" className="account-settings-button" aria-label={`${holiday.name} 삭제`} disabled={busy} onClick={() => void remove(holiday.id)}>삭제</button>
    </div>)}
  </>;
}
